import { Skeleton, Tooltip } from '@mui/material';
import { useGetBlobSasToken } from 'hooks/useGetBlobSasToken';
import React from 'react';

type BlurryLoadingImageProps = {
  src: string | undefined;
  alt?: string;
  imageStyle?: React.CSSProperties;
  bgColor?: string;
  fallback?: React.ReactNode;
  containerName?: string;
} & React.ImgHTMLAttributes<HTMLImageElement>;

const SparkImageLoader: React.FC<BlurryLoadingImageProps> = ({
  src,
  alt,
  imageStyle,
  bgColor = 'transparent',
  fallback,
  containerName,
  ...rest
}) => {
  const [currentImage, setCurrentImage] = React.useState<string | undefined>(
    undefined
  );
  const { refetch } = useGetBlobSasToken(containerName || '');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  const fetchImage = async (src: string | undefined) => {
    if (!src) {
      setLoading(false);
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);
    setCurrentImage(undefined);

    let imageSrc = src;
    if (containerName) {
      try {
        const { data } = await refetch();
        if (data?.data?.attributes?.token) {
          imageSrc = `${src}?${data.data.attributes.token}`;
        }
      } catch (error) {
        console.error('Failed to fetch SAS token:', error);
      }
    }

    const loadingImage = new Image();
    loadingImage.src = imageSrc;

    loadingImage.onload = () => {
      setCurrentImage(loadingImage.src);
      setLoading(false);
    };

    loadingImage.onerror = () => {
      setLoading(false);
      setError(true);
    };
  };

  React.useEffect(() => {
    fetchImage(src);
  }, [src]);

  if (loading) {
    return (
      <Skeleton
        variant='rectangular'
        height={rest.height}
        width={rest.width}
        sx={{ borderRadius: '10px' }}
      />
    );
  }

  if (error || !currentImage) {
    return fallback ? <>{fallback}</> : null;
  }

  return (
    <Tooltip title={alt || ''} sx={{ zIndex: 10000 }}>
      <img
        src={currentImage}
        alt={alt}
        style={{ backgroundColor: bgColor, ...imageStyle, ...rest.style }}
        {...rest}
      />
    </Tooltip>
  );
};

export default SparkImageLoader;
